import { addNotification } from "portal/state/notifications";
import { asBoolean, useQuery } from "portal/utils/hooks/useQuery";
import { Button, Checkbox, FormControlLabel } from "@mui/material";
import {
  carbonCropsEndpoints,
  CarbonCropStatus,
} from "portal/state/portal/carbonCrops";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { classes } from "portal/theme/theme";
import {
  CursorFetchArgs,
  CursorPageResponse,
  useCursorDataSource,
} from "portal/utils/hooks/useCursorDataSource";
import { DEBOUNCE_MS } from "portal/utils/timing";
import {
  emptyRowSelectionModel,
  rowSelectionModelToVisibleArray,
} from "portal/utils/gridRowSelection";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { NoScroll } from "portal/components/Page";
import { SearchField } from "portal/components/header/SearchField";
import { useDebounce } from "@uidotdev/usehooks";
import { useDispatch } from "react-redux";
import {
  useLazyPopups,
  useMutationPopups,
} from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import DisableIcon from "@mui/icons-material/CloseOutlined";
import EnableIcon from "@mui/icons-material/CheckOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";

const defaultColumn: Partial<GridColDef<CarbonCropStatus>> = {
  disableColumnMenu: true,
  flex: 1,
  sortable: true,
};

export type CustomerCarbonCropListProps = {
  customerUuid: string;
  canUpdate: boolean;
};

export const CustomerCarbonCropList: FC<CustomerCarbonCropListProps> = ({
  customerUuid,
  canUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebounce(searchInput.trim(), DEBOUNCE_MS);
  const [enabledOnly = false, setEnabledOnly] = useQuery(
    "enabledOnly",
    asBoolean
  );
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>(
    emptyRowSelectionModel()
  );
  // The grid keeps a controlled selection after rows leave the result set.
  // Clear it when search, the enabled filter, or the customer changes so
  // Enable/Disable selected cannot act on a hidden crop.
  const [selectionQuery, setSelectionQuery] = useState({
    customerUuid,
    enabledOnly,
    searchText,
  });
  if (
    selectionQuery.customerUuid !== customerUuid ||
    selectionQuery.enabledOnly !== enabledOnly ||
    selectionQuery.searchText !== searchText
  ) {
    setSelectionQuery({ customerUuid, enabledOnly, searchText });
    setSelectedIds(emptyRowSelectionModel());
  }
  const [disableLoading, setDisableLoading] = useState(false);
  const [enableLoading, setEnableLoading] = useState(false);

  const [listCustomerCarbonCropStatus] = useLazyPopups(
    carbonCropsEndpoints.listCustomerCarbonCropStatus.useLazyQuery()
  );
  const [setCustomerCarbonCropsEnabled] = useMutationPopups(
    carbonCropsEndpoints.setCustomerCarbonCropsEnabled.useMutation()
  );

  const fetchPage = useCallback(
    async ({
      cursor,
      limit,
    }: CursorFetchArgs): Promise<CursorPageResponse<CarbonCropStatus>> => {
      const { crops, nextPageToken } = await listCustomerCarbonCropStatus({
        customerUuid,
        limit,
        pageToken: cursor,
        search: searchText.trim() || undefined,
        enabledOnly,
      }).unwrap();
      return {
        rows: crops,
        pageInfo: {
          hasNextPage: Boolean(nextPageToken),
          nextCursor: nextPageToken || undefined,
        },
      };
    },
    [customerUuid, enabledOnly, listCustomerCarbonCropStatus, searchText]
  );

  const { dataGridProps, fetchedRows, reset } =
    useCursorDataSource<CarbonCropStatus>(fetchPage);

  const selectedCropIds = useMemo(
    () =>
      rowSelectionModelToVisibleArray(
        selectedIds,
        fetchedRows.map((crop) => crop.uuid)
      ).map(String),
    [fetchedRows, selectedIds]
  );

  const columns = useMemo(
    (): GridColDef<CarbonCropStatus>[] => [
      {
        ...defaultColumn,
        field: "name",
        flex: 2,
        minWidth: 280,
        headerName: t("models.carbonCrops.fields.name"),
      },
      {
        ...defaultColumn,
        field: "enabled",
        flex: 0.5,
        minWidth: 120,
        headerName: t("utils.descriptors.enabled"),
        valueGetter: (_value, row) =>
          row.enabled
            ? t("utils.descriptors.enabled")
            : t("utils.descriptors.disabled"),
        cellClassName: ({ row }) =>
          classes({
            "font-bold text-green-500": row.enabled,
            "text-gray-400": !row.enabled,
          }),
      },
    ],
    [t]
  );

  const toggleSelected = async (enabled: boolean): Promise<void> => {
    if (selectedCropIds.length === 0) {
      return;
    }
    await setCustomerCarbonCropsEnabled({
      customerUuid,
      cropIds: selectedCropIds,
      enabled,
    }).unwrap();
    dispatch(
      addNotification({
        message: enabled
          ? t("utils.actions.enableLong", {
              subject: t("utils.table.selected"),
            })
          : t("utils.actions.disableLong", {
              subject: t("utils.table.selected"),
            }),
        variant: "success",
      })
    );
    setSelectedIds(emptyRowSelectionModel());
    reset();
  };

  return (
    <NoScroll>
      <CarbonDataGrid<CarbonCropStatus>
        {...dataGridProps}
        key={customerUuid}
        header={
          <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-2">
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              label={t("utils.actions.searchLong", {
                subject: t("models.carbonCrops.carbonCrop_plural"),
              })}
              className="min-w-40 basis-full sm:flex-1 md:max-w-md"
            />
            <FormControlLabel
              className="m-0 max-w-full shrink-0"
              control={
                <Checkbox
                  checked={enabledOnly}
                  color="default"
                  onChange={(_event, checked) =>
                    setEnabledOnly(checked ? true : undefined)
                  }
                />
              }
              label={
                <span className="whitespace-normal">
                  {t("utils.actions.showLong", {
                    subject: t("utils.descriptors.enabled"),
                  })}
                </span>
              }
            />
            {canUpdate && (
              <div className="flex max-w-full shrink-0 flex-wrap gap-2">
                <Button
                  className="max-w-full whitespace-normal"
                  variant="contained"
                  color="error"
                  loading={disableLoading}
                  disabled={enableLoading || selectedCropIds.length === 0}
                  onClick={async () => {
                    setDisableLoading(true);
                    try {
                      await toggleSelected(false);
                    } finally {
                      setDisableLoading(false);
                    }
                  }}
                  startIcon={<DisableIcon />}
                >
                  {t("utils.actions.disableLong", {
                    subject: t("utils.table.selected"),
                  })}
                </Button>
                <Button
                  className="max-w-full whitespace-normal"
                  color="success"
                  variant="contained"
                  loading={enableLoading}
                  disabled={disableLoading || selectedCropIds.length === 0}
                  onClick={async () => {
                    setEnableLoading(true);
                    try {
                      await toggleSelected(true);
                    } finally {
                      setEnableLoading(false);
                    }
                  }}
                  startIcon={<EnableIcon />}
                >
                  {t("utils.actions.enableLong", {
                    subject: t("utils.table.selected"),
                  })}
                </Button>
              </div>
            )}
          </div>
        }
        className="flex flex-1"
        columns={columns}
        getRowId={(crop) => crop.uuid}
        initialState={{
          sorting: {
            sortModel: [{ field: "enabled", sort: "desc" }],
          },
        }}
        checkboxSelection={canUpdate}
        disableRowSelectionOnClick
        rowSelectionModel={selectedIds}
        onRowSelectionModelChange={setSelectedIds}
      />
    </NoScroll>
  );
};
